import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, IconButton, 
  Grid, Stack, Divider, Dialog, DialogActions, DialogTitle, DialogContent, 
  List, ListItem, ListItemButton, ListItemText, Avatar, ListItemAvatar, 
  GlobalStyles, Button
} from '@mui/material';

import SaveIcon from '@mui/icons-material/Save';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format, getDaysInMonth, setYear, setMonth, setDate, setHours, setMinutes } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useNavigate, useLocation } from 'react-router-dom';
import * as carApi from '../../lib/api/car';
import SlotWheel from '../common/util/SlotWheel'; 
import { differenceInMinutes } from 'date-fns';
import { useSnackbar } from 'notistack';

const CarReservForm = ({ 
  formik, carAssets, visitList, setVisitList, isListOpen, setIsListOpen, 
  isPartnerSelected, setIsPartnerSelected, partnerDisplayName, setPartnerDisplayName,
  isTimeOverlapping, setIsTimeOverlapping  
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [pickerTarget, setPickerTarget] = useState(null);
  const [isTimeConfirmed, setIsTimeConfirmed] = useState(false);

  const [localReservRows, setLocalReservRows] = useState(location.state?.reservData || []);

  const isEdit = location.state?.type === 'edit';
  const currentVal = pickerTarget === 'start' ? formik.values.startDate : formik.values.endDate;

  const handleCarChangeAndFetch = async (carUuid) => {
    // 1. 차량 UUID를 업데이트함과 동시에 기존 리스트를 즉시 비웁니다.
    formik.setFieldValue('carUuid', carUuid);
    setLocalReservRows([]); // <<-- 이 부분이 핵심입니다!
    setIsTimeConfirmed(false); 

    if (!carUuid) return;

    try {
      const res = await carApi.getCarReservList({ carUuid });
      // 2. 서버 응답이 왔을 때만 리스트를 채웁니다.
      setLocalReservRows(res.data?.list || []);
      setIsTimeConfirmed(true);
    } catch (err) {
      enqueueSnackbar('예약 현황 조회 실패', { variant: 'error' });
    }
  };

  const isTimeOverlappingLocal = useMemo(() => {
    if (!isTimeConfirmed || !formik.values.carUuid || !formik.values.startDate || !formik.values.endDate) return false;

    const parseDate = (date) => {
      if (date instanceof Date) return date.getTime();
      return new Date(String(date).replace(/-/g, '/')).getTime();
    };

    const currentStart = parseDate(formik.values.startDate);
    const currentEnd = parseDate(formik.values.endDate);

    const isLocalOverlap = localReservRows.some(res => {
      if (res.statusYn === 'N' || res.statusYn === 'E') return false; 

      const resStart = new Date(res.startDate.replace(/-/g, '/')).getTime();
      const resEnd = new Date(res.endDate.replace(/-/g, '/')).getTime();

      if (isNaN(resStart) || isNaN(resEnd)) return false;

      const isOverlapping = resStart < currentEnd && currentStart < resEnd;
      return isOverlapping;
    });

    const myOtherReservations = location.state?.reservData || [];
    const isMyOtherOverlap = myOtherReservations.some(res => {
      
      if (res.carUuid !== formik.values.carUuid && res.usrUuid === formik.values.usrUuid && ['S', 'Y'].includes(res.statusYn)) {
        const resStart = new Date(res.startDate).getTime();
        const resEnd = new Date(res.endDate).getTime();
        return resStart < currentEnd && currentStart < resEnd;
      }
      return false;
    });

    return isLocalOverlap || isMyOtherOverlap;

  }, [
    isTimeConfirmed, 
    formik.values.carUuid, 
    formik.values.startDate, 
    formik.values.endDate, 
    localReservRows, 
    location.state?.reservData // 의존성 배열에 추가
  ]);

  const years = useMemo(() => {
    const yr = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => (yr - 1) + i);
  }, []);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const daysInMonth = Array.from({ length: getDaysInMonth(currentVal) }, (_, i) => i + 1);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const updateDate = (type, value) => {
    let date = new Date(currentVal);
    if (type === 'year') date = setYear(date, value);
    else if (type === 'month') {
      const targetMonthDate = setMonth(date, value - 1);
      date = setDate(targetMonthDate, Math.min(date.getDate(), getDaysInMonth(targetMonthDate)));
    } 
    else if (type === 'day') date = setDate(date, value);
    else if (type === 'hour') date = setHours(date, value);
    else if (type === 'minute') date = setMinutes(date, value);

    if (pickerTarget === 'end' && date < formik.values.startDate) {
      alert("종료 시간은 시작 시간보다 빠를 수 없습니다.");
      formik.setFieldValue('endDate', new Date(formik.values.startDate));
      return; 
    }
    if (pickerTarget === 'start' && date >= formik.values.endDate) {
      formik.setFieldValue('startDate', date);
      formik.setFieldValue('endDate', new Date(date.getTime() + 60 * 60 * 1000));
      return;
    }
    formik.setFieldValue(pickerTarget === 'start' ? 'startDate' : 'endDate', date);
  };

  const handlePartnerChange = (e) => {
    setPartnerDisplayName(e.target.value);
    setIsPartnerSelected(false);
    formik.setFieldValue('visitPartner', '');
  };

  const handleOpenVisitList = async () => {
    if (!partnerDisplayName?.trim()) return alert("거래처명을 입력하세요.");
    try {
      const res = await carApi.getCarUsageVisitList({ partnerNm: partnerDisplayName });
      setVisitList(res.data?.list || res.data || []);
      setIsListOpen(true);
    } catch (e) { alert("조회 실패"); }
  };

  const handleSelectVisit = (p) => {
    const name = p.partnerNm || p.vendorNm || '';
    formik.setFieldValue('visitPartner', p.partnerUuid ? p.partnerUuid : 'newPlace_' + name);
    setPartnerDisplayName(name);
    setIsPartnerSelected(true);
    setIsListOpen(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('정말 예약을 취소하시겠습니까?')) return;
    try {
      const resUuid = location.state?.data?.logId; 
      if (!resUuid) return enqueueSnackbar('예약 정보를 찾을 수 없습니다.', { variant: 'error' });
      await carApi.reservCancel(resUuid);
      enqueueSnackbar('예약이 취소되었습니다.', { variant: 'success' });
      navigate(-1);
    } catch (err) { enqueueSnackbar('취소 중 오류가 발생했습니다.', { variant: 'error' }); }
  };

  // 다이얼로그 닫기 시 공통 처리 함수
  const closePicker = () => {
    setPickerTarget(null);
    setIsTimeConfirmed(true); // 어떤 방식으로 닫혀도 시간 확정으로 간주하여 중복 검사 실행
  };

  useEffect(() => {
    setIsTimeOverlapping(isTimeOverlappingLocal);
  }, [isTimeOverlappingLocal, setIsTimeOverlapping]);

  return (
    <>
      <GlobalStyles styles={{ 
              body: { backgroundColor: '#F8F9FA !important', margin: 0 },
              html: { backgroundColor: '#F8F9FA !important' } 
            }} />
      <Box sx={{ p: 1, minHeight: '100dvh' }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
              <IconButton onClick={() => navigate(-1)} size="small"><ArrowBackIosNewIcon sx={{ fontSize: 18 }} /></IconButton>
              <DirectionsCarIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>법인 차량 예약</Typography>
            </Stack>

            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6}><TextField fullWidth size="small" label="부서" value={formik.values.dptNm} InputProps={{ readOnly: true }} sx={{ bgcolor: '#f5f5f5' }} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="사용자" value={formik.values.usrNm} InputProps={{ readOnly: true }} sx={{ bgcolor: '#f5f5f5' }} /></Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth select size="small" label="예약 차량 선택" name="carUuid"
                    disabled={isEdit && !!formik.initialValues.carUuid} 
                    value={formik.values.carUuid}
                    onChange={(e) => handleCarChangeAndFetch(e.target.value)}
                    error={formik.touched.carUuid && Boolean(formik.errors.carUuid)}
                    helperText={formik.touched.carUuid && formik.errors.carUuid}
                  >
                    {carAssets.map((car) => (
                      <MenuItem key={car.carUuid} value={car.carUuid}>{car.carNumber}({car.carModel})</MenuItem>
                    ))}
                  </TextField>

                  {formik.values.carUuid && localReservRows.length > 0 && (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', ml: 0.5 }}>예약 현황</Typography>
                      {localReservRows.map((res, idx) => {
                        const curS = new Date(formik.values.startDate).getTime();
                        const curE = new Date(formik.values.endDate).getTime();
                        const resS = new Date(res.startDate).getTime();
                        const resE = new Date(res.endDate).getTime();
                        const isOver = isTimeConfirmed && res.statusYn !== 'N' && resS < curE && curS < resE;

                        // 요일이 포함된 날짜 문자열 생성 (한글 적용)
                        const formattedRange = `${format(new Date(res.startDate), 'yyyy-MM-dd(eee) HH:mm', { locale: ko })} ~ ${format(new Date(res.endDate), 'yyyy-MM-dd(eee) HH:mm', { locale: ko })}`;

                        return (
                          <Card key={idx} variant="outlined" sx={{ borderRadius: 2, bgcolor: isOver ? '#fff3e0' : (res.statusYn === 'N' ? '#fafafa' : '#fff'), borderColor: isOver ? '#ed6c02' : (res.statusYn === 'N' ? '#e0e0e0' : '#1976d2'), borderStyle: res.statusYn === 'N' ? 'dashed' : 'solid' }}>
                            <Box sx={{ p: 1.2 }}>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, color: isOver ? '#e65100' : (res.statusYn === 'N' ? 'text.disabled' : 'text.primary') }}>
                                  {res.usrNm} ({res.dptNm}) {isOver && "[중복]"}
                                </Typography>
                                <Box sx={{ 
                                  px: 1, py: 0.2, borderRadius: 1, fontSize: '0.65rem', fontWeight: 900, 
                                  bgcolor: res.statusYn === 'S' ? '#E1F5FE' : res.statusYn === 'E' ? '#F3E5F5' : res.statusYn === 'N' ? '#eeeeee' : '#E3F2FD',
                                  color: res.statusYn === 'S' ? '#0288D1' : res.statusYn === 'E' ? '#7B1FA2' : res.statusYn === 'N' ? '#9e9e9e' : '#1976D2'
                                }}>
                                  {res.statusYn === 'S' ? '운행 중' : res.statusYn === 'E' ? '반납 완료' : res.statusYn === 'N' ? '예약 취소' : '예약'}
                                </Box>
                              </Stack>
                              <Box sx={{ mt: 0.5 }}>
                                <Typography 
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    fontWeight: 800, 
                                    color: isOver ? '#e65100' : 'text.secondary', 
                                    display: 'block' 
                                  }}
                                >
                                  예약기간
                                </Typography>
                                <Typography 
                                  sx={{ 
                                    fontSize: '0.75rem', 
                                    fontWeight: 700, 
                                    color: isOver ? '#e65100' : 'primary.main', // 중복 시 주황색, 평소엔 파란색
                                    lineHeight: 1.4 
                                  }}
                                >
                                  {formattedRange}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                  {/* 나의 다른 예약 현황 섹션 */}
                  {location.state?.reservData?.length > 0 && (() => {
                    // 1. 보여줄 데이터를 미리 필터링 (현재 차량 제외 + 본인 예약 + S,Y 상태)
                    const myOtherReservList = location.state.reservData.filter(res => 
                      ['S', 'Y'].includes(res.statusYn) && 
                      res.carUuid !== formik.values.carUuid && // 차량 ID가 다를 때만!
                      res.usrUuid === formik.values.usrUuid
                    );

                    // 2. 필터링된 데이터가 있을 때만 레이블과 리스트 렌더링
                    if (myOtherReservList.length === 0) return null;

                    return (
                      <Stack spacing={1} sx={{ mt: 3 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'success.main', ml: 0.5 }}>
                          나의 다른 예약 현황
                        </Typography>
                        
                        {myOtherReservList.map((res, idx) => {
                          const curS = new Date(formik.values.startDate).getTime();
                          const curE = new Date(formik.values.endDate).getTime();
                          const resS = new Date(res.startDate).getTime();
                          const resE = new Date(res.endDate).getTime();
                          
                          const isTimeOverlappingLocal = isTimeConfirmed && resS < curE && curS < resE;
                          const formattedRange = `${format(new Date(res.startDate), 'yyyy-MM-dd(eee) HH:mm', { locale: ko })} ~ ${format(new Date(res.endDate), 'yyyy-MM-dd(eee) HH:mm', { locale: ko })}`;

                          return (
                            <Card 
                              key={`my-other-${idx}`} 
                              variant="outlined" 
                              sx={{ 
                                borderRadius: 2, 
                                bgcolor: isTimeOverlappingLocal ? '#fff3e0' : '#f1f8e9', 
                                borderColor: isTimeOverlappingLocal ? '#ed6c02' : '#81c784',
                                borderWidth: isTimeOverlappingLocal ? 2 : 1
                              }}
                            >
                              <Box sx={{ p: 1.2 }}>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                  <Typography sx={{ 
                                    fontSize: '0.8rem', 
                                    fontWeight: 900, 
                                    color: isTimeOverlappingLocal ? '#e65100' : 'success.dark' 
                                  }}>
                                    {res.carNumber} ({res.carModel}) {isTimeOverlappingLocal && "[중복]"}
                                  </Typography>
                                  <Box sx={{ 
                                    px: 1, py: 0.2, borderRadius: 1, fontSize: '0.65rem', fontWeight: 900, 
                                    bgcolor: isTimeOverlappingLocal ? '#ed6c02' : (res.statusYn === 'S' ? '#E1F5FE' : '#C8E6C9'), 
                                    color: isTimeOverlappingLocal ? '#fff' : (res.statusYn === 'S' ? '#0288D1' : '#2E7D32') 
                                  }}>
                                    {isTimeOverlappingLocal ? '시간중복' : (res.statusYn === 'S' ? '운행 중' : '예약')}
                                  </Box>
                                </Stack>
                                
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'text.secondary', display: 'block' }}>
                                    예약기간
                                  </Typography>
                                  <Typography sx={{ 
                                    fontSize: '0.75rem', 
                                    fontWeight: 700, 
                                    color: isTimeOverlappingLocal ? '#e65100' : 'success.dark', 
                                    lineHeight: 1.4 
                                  }}>
                                    {formattedRange}
                                  </Typography>
                                </Box>
                              </Box>
                            </Card>
                          );
                        })}
                      </Stack>
                    );
                  })()}

                </Grid>

                <Grid item xs={12}>
                  {isTimeConfirmed && isTimeOverlappingLocal && (
                    <Typography variant="caption" sx={{ color: '#ed6c02', fontWeight: 900, ml: 0.5, mb: 1, display: 'block' }}>⚠️ 선택하신 일정이 다른 예약 일정과 겹칩니다!</Typography>
                  )}
                  <Box 
                    onClick={() => {
                      if(!isEdit) {
                        setPickerTarget('start');
                        setIsTimeConfirmed(false);
                      }
                    }}
                    sx={{ 
                      position: 'relative', p: '14px 10px', 
                      border: (isTimeConfirmed && isTimeOverlappingLocal) ? '2px solid #ed6c02' : '1px solid #ccc', 
                      borderRadius: 1.5, bgcolor: isEdit ? '#f5f5f5' : '#fff', cursor: isEdit ? 'default' : 'pointer', 
                      pointerEvents: isEdit ? 'none' : 'auto', opacity: isEdit ? 0.8 : 1, transition: 'border 0.2s ease'
                    }}
                  >
                    <Typography variant="caption" sx={{ position: 'absolute', top: -10, left: 10, px: 0.5, bgcolor: isEdit ? '#f5f5f5' : '#fff', color: (isTimeConfirmed && isTimeOverlappingLocal) ? '#ed6c02' : 'text.secondary', fontWeight: 700, fontSize: '0.75rem', zIndex: 1 }}>예약 기간</Typography>
                    <Grid container alignItems="center">
                      <Grid item xs={5.5} sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700 }}>{format(formik.values.startDate, 'yyyy-MM-dd')}</Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: isEdit ? 'text.secondary' : 'primary.main', fontWeight: 800 }}>{format(formik.values.startDate, '(eee) HH:mm', { locale: ko })}</Typography>
                      </Grid>
                      <Grid item xs={1} sx={{ textAlign: 'center' }}><Typography sx={{ color: 'text.disabled', fontWeight: 900 }}>~</Typography></Grid>
                      <Grid item xs={5.5} sx={{ textAlign: 'center' }} onClick={(e) => { if(!isEdit) { e.stopPropagation(); setPickerTarget('end'); setIsTimeConfirmed(false); } }}>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700 }}>{format(formik.values.endDate, 'yyyy-MM-dd')}</Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: isEdit ? 'text.secondary' : 'primary.main', fontWeight: 800 }}>{format(formik.values.endDate, '(eee) HH:mm', { locale: ko })}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="방문 거래처" value={partnerDisplayName} onChange={handlePartnerChange} disabled={isEdit}
                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: isEdit ? '#f5f5f5' : (isPartnerSelected ? '#f0f7ff' : 'inherit') } }}
                    InputProps={{ endAdornment: (
                      <IconButton size="small" onClick={handleOpenVisitList} disabled={isEdit}>
                        {isPartnerSelected ? <CheckCircleIcon color={isEdit ? "disabled" : "success"} /> : <SearchIcon />}
                      </IconButton>
                    ) }}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                {isEdit ? (
                  <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      color="error" 
                      size="large" 
                      // 'N'(취소됨), 'S'(운행 중), 'E'(운행 종료) 상태 모두 버튼 비활성화
                      disabled={['N', 'S', 'E'].includes(location.state?.data?.statusYn)} 
                      sx={{ height: 52, fontWeight: 800, borderRadius: 2 }} 
                      onClick={handleDelete}
                    >
                      {
                        location.state?.data?.statusYn === 'N' ? '취소된 예약' : 
                        location.state?.data?.statusYn === 'S' ? '운행 중 (취소 불가)' : 
                        location.state?.data?.statusYn === 'E' ? '예약 종료' : 
                        '예약 취소'
                      }
                    </Button>

                  </Stack>
                ) : (
                  <Button fullWidth type="submit" variant="contained" size="large" startIcon={<SaveIcon />} disabled={isTimeOverlappingLocal || !formik.values.carUuid} sx={{ mt: 3, height: 52, fontWeight: 800, borderRadius: 2 }}>
                    {!formik.values.carUuid ? '차량을 선택해주세요' : isTimeOverlappingLocal ? '일정 중복으로 예약 불가' : '예약하기'}
                  </Button>
                )}
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* Picker Dialog - onClose 수정됨 */}
        <Dialog 
          open={Boolean(pickerTarget)} 
          onClose={closePicker} 
          fullWidth maxWidth="xs" 
          PaperProps={{ sx: { m: 2, borderRadius: 3, overflow: 'hidden' } }}
        >
          <Stack direction="row" sx={{ bgcolor: '#f8f9fa', p: 1, borderBottom: '1px solid #eee' }}>
            <Button fullWidth variant={pickerTarget === 'start' ? 'contained' : 'text'} onClick={() => setPickerTarget('start')} sx={{ borderRadius: 2, fontWeight: 800 }}>시작</Button>
            <Button fullWidth variant={pickerTarget === 'end' ? 'contained' : 'text'} onClick={() => setPickerTarget('end')} sx={{ borderRadius: 2, fontWeight: 800 }}>종료</Button>
          </Stack>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 800, mb: 2 }}>{format(currentVal, 'yyyy년 MM월 dd일 (eee) HH:mm', { locale: ko })}</Typography>
            <Stack direction="row" spacing={0.5}>
              <SlotWheel items={years} currentValue={currentVal.getFullYear()} onSelect={(v) => updateDate('year', v)} unit="년" />
              <SlotWheel items={months} currentValue={currentVal.getMonth() + 1} onSelect={(v) => updateDate('month', v)} unit="월" />
              <SlotWheel items={daysInMonth} currentValue={currentVal.getDate()} onSelect={(v) => updateDate('day', v)} unit="일" />
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1}>
              <SlotWheel items={hours} currentValue={currentVal.getHours()} onSelect={(v) => updateDate('hour', v)} unit="시" />
              <SlotWheel items={minutes} currentValue={currentVal.getMinutes()} onSelect={(v) => updateDate('minute', v)} unit="분" />
            </Stack>
          </Box>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={closePicker} variant="contained" fullWidth sx={{ height: 48, borderRadius: 2, fontWeight: 800 }}>설정 완료</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isListOpen} onClose={() => setIsListOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2, borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', fontSize: '1rem' }}>거래처 검색 결과</DialogTitle>
          <DialogContent dividers sx={{ p: 0, maxHeight: '50vh' }}>
            <List>
              {visitList.length > 0 ? visitList.map((item, index) => (
                <ListItem key={index} disablePadding divider>
                  <ListItemButton onClick={() => handleSelectVisit(item)} sx={{ py: 1.5 }}>
                    <ListItemAvatar><Avatar sx={{ width: 32, height: 32 }}><BusinessIcon sx={{ fontSize: 18 }} /></Avatar></ListItemAvatar>
                    <ListItemText primary={item.partnerNm || item.vendorNm} secondary={item.partnerAddr} primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }} secondaryTypographyProps={{ fontSize: '0.75rem' }} />
                  </ListItemButton>
                </ListItem>
              )) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>"{partnerDisplayName}"을(를) 등록할까요?</Typography>
                  <Stack direction="row" spacing={1}><Button fullWidth variant="contained" onClick={() => handleSelectVisit({ partnerNm: partnerDisplayName })}>예</Button><Button fullWidth variant="outlined" onClick={() => setIsListOpen(false)}>아니오</Button></Stack>
                </Box>
              )}
            </List>
          </DialogContent>
          <DialogActions><Button onClick={() => setIsListOpen(false)} fullWidth color="inherit">닫기</Button></DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CarReservForm;
